/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Spinner } from 'reactstrap';
import * as default_data from '../config.js'
import { Alert, Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { getBannerColor } from '../Utils/classification_helper.js';
import { getLoginLogo } from '../Utils/file_functions.js';

function VerifyForm() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);

    fetch(default_data.default_user_service_url_no_cac + '/verify-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        verification_code: code
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        console.log(response)
        throw new Error('An error occurred while logging in.');
      })
      .then(data => {
        if (data.status != 200) {
          throw new Error(data.response);
        }
        data = data.response;

        setIsLoading(false);
        setErrorMessage('');

        setSuccessMessage("Your account has been registered!");

        const sleep = async (milliseconds) => {
          await new Promise(resolve => {
            return setTimeout(resolve, milliseconds)
          });
        };
        const run_sleep = async () => {
          await sleep(2000);
          window.location.href = '/login';
        }
        run_sleep();
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
        setErrorMessage(error.message);
      });
  };

  return (
    <div>
      {
        <div className={`header-message ${getBannerColor(false)}`}>
          {default_data.default_banner_override != null && default_data.default_banner_override != '' ?
            default_data.default_banner_override : 'UNCLASSIFIED'
          }
        </div>
      }
      <div style={{ backgroundColor: '#121212', height: 'calc(100vh - 16px)' }}>
        <Stack sx={{ maxHeight: 'calc(100vh - 16px)' }} direction={"row"} spacing={0}>
          <Box sx={{ width: isTablet ? "100vw" : "50vw", overflowY: 'auto' }} >
            <Container>
              <Box style={{ display: 'flex', justifyContent: isTablet ? 'center' : 'start', marginBottom: '8px', marginTop: '32px' }}>
                <a href="/">
                  <img src="/logo.png" style={{ width: isTablet ? '90px' : '120px' }} alt="Ask Sage" className="logo" />
                </a>
              </Box>
              {/* Form Body */}
              <Row className="justify-content-center ">
                <Col xs="12" md="8" lg="8">
                  <Typography
                    variant='h5'
                    sx={{
                      color: 'white',
                      fontWeight: '500',
                      textAlign: { xs: 'center', tablet: 'start' },
                      fontSize: { xs: 'calc(1.325rem + .9vw)', tablet: '2rem' },
                    }}
                  >
                    Verify your email
                  </Typography>
                  <Typography
                    variant='p'
                    sx={{
                      color: 'lightgray',
                      textAlign: { xs: 'center', tablet: 'start' },
                      fontSize: '14px',
                    }}
                  >
                    You should have received a code via email, check your spam folder! If you did not receive an email, please email us at {default_data.default_support_email}.
                  </Typography>

                  <Form onSubmit={handleSubmit} className='login-form'>
                    <FormGroup>
                      <Label for="email">Email</Label>
                      <Input type="email" name="email" id="email" value={email} onChange={event => setEmail(event.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label for="password">Verification code</Label>
                      <Input type="text" name="code" id="code" value={code} onChange={event => setCode(event.target.value)} />
                    </FormGroup>
                    {errorMessage && <Alert severity='error' sx={{mb: 2}}>{errorMessage}</Alert>}
                    {successMessage && <Alert severity='success' sx={{mb: 2}}>{successMessage}</Alert>}
                    <Button type="submit" disabled={isLoading} className='login-form-button'>
                      {isLoading ? <Spinner size="sm" /> : 'Login'}
                    </Button>
                  </Form>
                </Col>
              </Row>
              <Row className="justify-content-center reg-page-mode-footer">
                <Col xs="12" md="12" lg="12">
                  Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
                </Col>
              </Row>
            </Container>
          </Box>
          {!isTablet && (
            <div
              className="space-image"
              style={{
                backgroundImage: "url('/space-background.png')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                width: '50vw',
              }}
            >
              <img src={getLoginLogo()} alt="Space Girl" />
            </div>
          )}
        </Stack>
      </div>
    </div>
  );
}

export default VerifyForm;
